import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eraser, Palette } from 'lucide-react';

const COLORS = [
  { name: 'Sand', color: '#F4A460' },
  { name: 'Water', color: '#4682B4' },
  { name: 'Fire', color: '#FF4500' },
  { name: 'Plant', color: '#32CD32' },
  { name: 'Stone', color: '#708090' }
];

const GravitySand = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const gridRef = useRef(null);
  const movedRef = useRef(null);
  const animationRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [isDrawing, setIsDrawing] = useState(false);
  const cellSize = 4;
  const cols = useRef(0);
  const rows = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = Math.floor(window.innerWidth / cellSize) * cellSize;
    canvas.height = Math.floor(window.innerHeight / cellSize) * cellSize;
    
    cols.current = Math.floor(canvas.width / cellSize);
    rows.current = Math.floor(canvas.height / cellSize);
    
    gridRef.current = Array(cols.current).fill().map(() => Array(rows.current).fill(null));
    movedRef.current = Array(cols.current).fill().map(() => new Uint8Array(rows.current));

    const resize = () => {
      const oldGrid = gridRef.current;
      const oldCols = cols.current;
      const oldRows = rows.current;

      canvas.width = Math.floor(window.innerWidth / cellSize) * cellSize;
      canvas.height = Math.floor(window.innerHeight / cellSize) * cellSize;
      cols.current = Math.floor(canvas.width / cellSize);
      rows.current = Math.floor(canvas.height / cellSize);

      // Preserve existing particles instead of wiping the drawing on resize
      const newGrid = Array(cols.current).fill().map(() => Array(rows.current).fill(null));
      const copyCols = Math.min(oldCols, cols.current);
      const copyRows = Math.min(oldRows, rows.current);
      for (let x = 0; x < copyCols; x++) {
        for (let y = 0; y < copyRows; y++) {
          newGrid[x][y] = oldGrid[x][y];
        }
      }
      gridRef.current = newGrid;
      movedRef.current = Array(cols.current).fill().map(() => new Uint8Array(rows.current));
    };
    window.addEventListener('resize', resize);

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const grid = gridRef.current;
    const moved = movedRef.current;

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Reset move-tracking for this frame so each particle is only ever
    // stepped once per frame, even if it lands in a column that hasn't
    // been visited yet this pass.
    for (let x = 0; x < cols.current; x++) {
      moved[x].fill(0);
    }

    // Update sand physics
    for (let x = 0; x < cols.current; x++) {
      for (let y = rows.current - 2; y >= 0; y--) {
        if (grid[x][y] && !moved[x][y]) {
          const below = y + 1;
          const above = y - 1;
          const color = grid[x][y];

          if (color === '#708090') { // Stone: solid, does not move
            // no-op, acts as an inert obstacle for everything else
          } else if (color === '#4682B4') { // Water: falls, and spreads sideways when blocked
            if (!grid[x][below]) {
              grid[x][below] = color;
              grid[x][y] = null;
              moved[x][below] = 1;
            } else {
              const dir = Math.random() > 0.5 ? 1 : -1;
              if (x + dir >= 0 && x + dir < cols.current && !grid[x + dir][y]) {
                grid[x + dir][y] = color;
                grid[x][y] = null;
                moved[x + dir][y] = 1;
              }
            }
          } else if (color === '#FF4500') { // Fire: rises, and is extinguished by nearby water
            const neighbors = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]];
            const nearWater = neighbors.some(([nx, ny]) =>
              nx >= 0 && nx < cols.current && ny >= 0 && ny < rows.current && grid[nx][ny] === '#4682B4'
            );
            if (nearWater) {
              grid[x][y] = null;
            } else if (above >= 0 && !grid[x][above]) {
              grid[x][above] = color;
              grid[x][y] = null;
              moved[x][above] = 1;
            } else {
              const dir = Math.random() > 0.5 ? 1 : -1;
              if (above >= 0 && x + dir >= 0 && x + dir < cols.current && !grid[x + dir][above]) {
                grid[x + dir][above] = color;
                grid[x][y] = null;
                moved[x + dir][above] = 1;
              }
            }
          } else if (color === '#32CD32') { // Plant: falls until it rests, then slowly grows upward
            if (!grid[x][below]) {
              grid[x][below] = color;
              grid[x][y] = null;
              moved[x][below] = 1;
            } else if (above >= 0 && !grid[x][above] && Math.random() < 0.01) {
              grid[x][above] = color;
              moved[x][above] = 1;
            }
          } else { // Sand: falls straight down, or diagonally when blocked
            if (!grid[x][below]) {
              grid[x][below] = color;
              grid[x][y] = null;
              moved[x][below] = 1;
            } else {
              const dir = Math.random() > 0.5 ? 1 : -1;
              if (x + dir >= 0 && x + dir < cols.current && !grid[x + dir][below]) {
                grid[x + dir][below] = color;
                grid[x][y] = null;
                moved[x + dir][below] = 1;
              }
            }
          }
        }
      }
    }

    // Draw grid
    for (let x = 0; x < cols.current; x++) {
      for (let y = 0; y < rows.current; y++) {
        if (grid[x][y]) {
          ctx.fillStyle = grid[x][y];
          ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
        }
      }
    }

    animationRef.current = requestAnimationFrame(animate);
  };

  const addParticle = (clientX, clientY) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = Math.floor((clientX - rect.left) / cellSize);
    const y = Math.floor((clientY - rect.top) / cellSize);

    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < cols.current && ny >= 0 && ny < rows.current) {
          gridRef.current[nx][ny] = selectedColor.color;
        }
      }
    }
  };

  const handlePointerDown = (e) => {
    setIsDrawing(true);
    addParticle(e.clientX, e.clientY);
  };

  const handlePointerMove = (e) => {
    if (isDrawing) {
      addParticle(e.clientX, e.clientY);
    }
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    gridRef.current = Array(cols.current).fill().map(() => Array(rows.current).fill(null));
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] relative overflow-hidden">
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className="absolute inset-0 cursor-crosshair"
        style={{ touchAction: 'none' }}
      />

      <div className="relative z-10 p-6">
        <header className="flex justify-between items-center mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-4xl font-bold text-white">Gravity Sand</h1>
          
          <button
            data-testid="clear-canvas"
            onClick={clearCanvas}
            className="glass-effect text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
          >
            <Eraser className="w-5 h-5" />
            Clear
          </button>
        </header>

        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 glass-effect rounded-2xl p-4">
          <div className="flex items-center gap-4">
            <Palette className="w-6 h-6 text-white" />
            {COLORS.map(item => (
              <button
                key={item.name}
                data-testid={`color-${item.name.toLowerCase()}`}
                onClick={() => setSelectedColor(item)}
                className={`w-12 h-12 rounded-full transition-all duration-300 ${
                  selectedColor.name === item.name ? 'ring-4 ring-white scale-110' : 'hover:scale-105'
                }`}
                style={{ backgroundColor: item.color }}
                title={item.name}
              />
            ))}
          </div>
        </div>

        <div className="fixed top-32 left-8 glass-effect rounded-2xl p-4 text-white max-w-xs">
          <p className="text-sm">
            Draw with your pointer to create falling particles. Different materials behave differently!
          </p>
        </div>
      </div>
    </div>
  );
};

export default GravitySand;